import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { api } from '../../../lib/api.js';

interface BillingResponse {
  plan?: {
    name: string;
    module: string;
    renders: number;
    hits: number;
    cost: number;
    ended_on?: string | null;
  } | null;
  orders: Array<{
    txn: string;
    purchase_date: string;
    status: string;
    plan_details?: { name?: string; cost?: number };
  }>;
}

export function BillingPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { data, isPending } = useQuery({
    queryKey: ['billing', id],
    queryFn: () =>
      api.get<BillingResponse>(`/api/workspaces/${encodeURIComponent(id)}/billing`),
  });
  if (isPending) return <div className="card text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-4">
      {data?.plan && (
        <div className="card">
          <h3 className="text-base font-semibold">Current plan</h3>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-slate-500">Plan</dt>
            <dd>{data.plan.name}</dd>
            <dt className="text-slate-500">Module</dt>
            <dd>{data.plan.module}</dd>
            <dt className="text-slate-500">Quota (renders / hits)</dt>
            <dd>
              {data.plan.renders.toLocaleString()} / {data.plan.hits.toLocaleString()}
            </dd>
            <dt className="text-slate-500">Cost</dt>
            <dd>{data.plan.cost}</dd>
            <dt className="text-slate-500">Ends</dt>
            <dd>{data.plan.ended_on ?? '—'}</dd>
          </dl>
        </div>
      )}
      <div className="card">
        <h3 className="text-base font-semibold">Orders</h3>
        <table className="mt-3 min-w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2">Date</th>
              <th className="text-left py-2">Plan</th>
              <th className="text-left py-2">Status</th>
              <th className="text-right py-2">Cost</th>
              <th className="text-left py-2">Txn</th>
            </tr>
          </thead>
          <tbody>
            {(data?.orders ?? []).map((o) => (
              <tr key={o.txn} className="border-b last:border-b-0">
                <td className="py-2">{o.purchase_date}</td>
                <td className="py-2">{o.plan_details?.name ?? '—'}</td>
                <td className="py-2">{o.status}</td>
                <td className="py-2 text-right">{o.plan_details?.cost ?? '—'}</td>
                <td className="py-2 font-mono text-xs">{o.txn}</td>
              </tr>
            ))}
            {(!data?.orders || data.orders.length === 0) && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-slate-400">
                  No orders yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
